import React, { CSSProperties, useRef, useState } from "react";
import FocusLock from "react-focus-lock";
import classname from "classnames";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/swiper.min.css";

import { useDeviceVh } from "@hooks";

import { useLightbox } from "./hooks";
import { SLIDE_TRANSITION_DURATION_MS } from "./constants";
import { IconButton, SlideItem } from "./components";

// Install Swiper modules
SwiperCore.use([Navigation]);

const GalleryLightbox = () => {
  const focusLockRef = useRef<HTMLDivElement>(null);
  const swiperContainerRef = useRef<HTMLDivElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>(null);

  const { deviceVh } = useDeviceVh();

  const {
    clickedThumbnailIndex,
    handleCloseButtonClick,
    handleLightboxFading,
    activeContentHeight,
    activeDescriptionHeight,
    setActiveDescriptionRef,
    setActiveContentRef,
    isClosing,
    isOpen,
    isOpening,
    items,
    localization,
  } = useLightbox({ focusLockRef, swiperInstance });

  const parentClasses = classname("lightbox", {
    "lightbox--open": isOpen,
    "lightbox--closing": isClosing,
    "lightbox--closed": !isOpen && !isOpening,
  });

  const handleOnBeforeInit = (swiper: SwiperCore): void => {
    const { navigation } = swiper.params;

    if (typeof navigation === "boolean") return;

    navigation.prevEl = prevButtonRef.current;
    navigation.nextEl = nextButtonRef.current;
  };

  return (
    <FocusLock disabled={!isOpen} ref={focusLockRef}>
      {!!items?.length && (
        <div
          className={parentClasses}
          onTransitionEnd={handleLightboxFading}
          style={
            {
              "--device-vh": `${deviceVh}px`,
              "--active-description-height": `${activeDescriptionHeight}px`,
              "--active-content-height": `${activeContentHeight}px`,
            } as CSSProperties
          }
          tabIndex={-1}
          role="presentation"
          ref={swiperContainerRef}
        >
          <div className="lightbox__inner-wrapper">
            <Swiper
              className="lightbox__swiper"
              enabled={items?.length > 1}
              // 2rem
              spaceBetween={32}
              loop
              longSwipesRatio={0.2}
              resizeObserver
              onBeforeInit={handleOnBeforeInit}
              speed={SLIDE_TRANSITION_DURATION_MS}
              onSwiper={setSwiperInstance}
            >
              <div slot="container-start" className="lightbox__topbar">
                <IconButton
                  ariaLabel={localization?.CLOSE_TITLE}
                  type="close"
                  clickHandler={handleCloseButtonClick}
                />
              </div>

              {items.map((item) => (
                <SwiperSlide key={item.slidePositionIndex}>
                  {({ isActive }) => (
                    <SlideItem
                      isActive={isActive}
                      swiperContainerRef={swiperContainerRef}
                      item={item}
                      clickedThumbnailIndex={clickedThumbnailIndex}
                      isLighthboxOpen={isOpen}
                      setActiveDescriptionRef={setActiveDescriptionRef}
                      setActiveContentRef={setActiveContentRef}
                    />
                  )}
                </SwiperSlide>
              ))}

              {items.length > 1 && (
                <>
                  <IconButton
                    ariaLabel={localization?.PREVIOUS_TITLE}
                    buttonRef={prevButtonRef}
                    type="previous"
                  />
                  <IconButton
                    ariaLabel={localization?.NEXT_TITLE}
                    buttonRef={nextButtonRef}
                    type="next"
                  />
                </>
              )}
            </Swiper>
          </div>
        </div>
      )}
    </FocusLock>
  );
};

export default GalleryLightbox;
